import SelectField from '@/components/select-field'
import {
  QUEST_TYPE_KEYS,
  QuestConfigDto,
  QuestTypeKey,
  emptyQuestConfig,
} from '@/common/models/gamification'

import { AvatarSelectConfigFields } from './AvatarSelectConfigFields'
import { ContinuousSpendingConfigFields } from './ContinuousSpendingConfigFields'
import { ContinuousTopupConfigFields } from './ContinuousTopupConfigFields'
import { QrScanningConfigFields } from './QrScanningConfigFields'
import { TopupConfigFields } from './TopupConfigFields'

interface QuestConfigFieldsProps {
  value: QuestConfigDto
  onChange: (next: QuestConfigDto) => void
  /**
   * The server-provided QR_SCANNING deep link (edit mode, once saved) — enables the deep-link + QR
   * preview. Passed through verbatim; never reconstructed here.
   */
  deepLink?: string | null
  /** Quest id — used only to name the downloaded QR file (never to build the deep link). */
  questId?: string
  /** Festival edition id — needed by the continuous quest types' token-pool picker. */
  editionId: string
}

/**
 * Editor for a quest's config, polymorphic on `typeKey`. The type dropdown selects the quest
 * type and switching type resets the config to that type's defaults. Each type renders its own
 * fields component.
 */
export const QuestConfigFields = ({
  value,
  onChange,
  deepLink,
  questId,
  editionId,
}: QuestConfigFieldsProps) => {
  const renderTypeFields = () => {
    switch (value.typeKey) {
      case 'TOPUP':
        return <TopupConfigFields value={value} onChange={onChange} />
      case 'CONTINUOUS_TOPUP':
        return (
          <ContinuousTopupConfigFields
            value={value}
            onChange={onChange}
            editionId={editionId}
          />
        )
      case 'CONTINUOUS_SPENDING':
        return (
          <ContinuousSpendingConfigFields
            value={value}
            onChange={onChange}
            editionId={editionId}
          />
        )
      case 'AVATAR_SELECT':
        return <AvatarSelectConfigFields value={value} onChange={onChange} />
      case 'QR_SCANNING':
        return (
          <QrScanningConfigFields
            value={value}
            onChange={onChange}
            deepLink={deepLink}
            questId={questId}
          />
        )
    }
  }

  // Every quest type is always offered. A continuous type is NOT blocked outright just because the
  // edition already has one: the limit is one *open at a time*, so a second is legitimate with a
  // non-overlapping time window (e.g. a different rate per festival day). Whether this particular
  // placement conflicts depends on the window, which lives on the quest rather than its config —
  // QuestEditorModal owns that check and reports it against the date fields.
  const typeOptions = QUEST_TYPE_KEYS.map((k) => ({ value: k, label: k }))

  return (
    <div className="space-y-6 border-t border-monochrome-120 pt-6">
      <div className="text-lg font-bold text-monochrome-1">Quest config</div>

      <div>
        <SelectField
          label="Type"
          required
          placeholder="Select quest type"
          value={value.typeKey}
          onValueChange={(v) => onChange(emptyQuestConfig(v as QuestTypeKey))}
          options={typeOptions}
        />
      </div>

      {renderTypeFields()}
    </div>
  )
}
